import * as React from "react";
import { Alert, Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { AppSession } from "src/models/AppSession";
import { ILicence, ILoginLibrary, InviteType } from "src/models/dto/DashboardModels";
import { AppContext } from "src/ui/state/Contextes";
import { Convert } from "src/utilities/Helpers";

import * as Messages from "../Messages";

export interface ILicenceInviteFormProps {
  licence: ILicence;
  wrapInForm?: boolean;
  goBackBtnClicked: () => void;
  availableLicenses: number;
}
export interface ILicenceInviteFormState {
  currentInviteType: InviteType;
  userId: number | null;
  userName: string | null;
  email: string | null;
  publisherId: number | null;
  completedLink: string | null;
  bulkInviteEmails: string[];
  bulkInviteErrors: string[];
  pendingInvitationEmails: string[];
  assignToExistingUsers: boolean;
}
export class LicenceInviteForm extends React.Component<ILicenceInviteFormProps, ILicenceInviteFormState> {
  context: AppSession;
  static contextType = AppContext;
  linkRef = React.createRef<HTMLInputElement>();
  constructor(props: ILicenceInviteFormProps) {
    super(props);
    this.state = {
      currentInviteType: InviteType.DirectCreateUsername,
      email: null,
      userId: null,
      userName: null,
      publisherId: null,
      completedLink: null,
      bulkInviteEmails: [],
      bulkInviteErrors: [],
      assignToExistingUsers: false,
      pendingInvitationEmails: [],
    };
  }
  componentDidMount() {
    let pub = this.context.loginLibraries.get("prolibro");
    if (!pub) {
      pub = this.context.loginLibraries.rows()[0];
    }
    this.setState({ publisherId: pub.PublisherId });
  }
  componentWillUnmount() {}
  inviteTypeChanged = (arg: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ currentInviteType: +arg.target.value });
  };
  userIdChanged = (arg: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ userId: +arg.target.value });
  };
  usernameChanged = (arg: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ userName: arg.target.value });
  };
  directCreateUserIdLicence = async () => {
    let response = await this.context.insertLicence({ Licence: this.props.licence });
    if (response.valid()) {
      Messages.Notify.success(`The Licence item was saved successfully!`);
      if (this.props.goBackBtnClicked) {
        this.props.goBackBtnClicked();
      }
    } else {
      if (response.errors.length > 0) {
        Messages.Notify.error("Save failed. Server reported: " + response.errors[0].Message);
      } else {
        Messages.Notify.error("An error occurred while executing the communication");
      }
    }
  };
  directCreateUsername = async () => {
    let response = await this.context.createLicenceByUsername({
      Licence: this.props.licence,
      Username: this.state.userName!,
      PublisherId: this.state.publisherId!,
    });
    if (response.valid()) {
      Messages.Notify.success(`The Licence item was saved successfully!`);
      if (this.props.goBackBtnClicked) {
        this.props.goBackBtnClicked();
      }
    } else {
      if (response.errors.length > 0) {
        Messages.Notify.error("Save failed. Server reported: " + response.errors[0].Message);
      } else {
        Messages.Notify.error("An error occurred while executing the communication");
      }
    }
  };

  inviteEmail = async () => {
    if (this.state.email === null || Convert.isEmptyOrSpaces(this.state.email)) {
      Messages.Notify.error("Please enter an email in the field to send the invite.");
      return;
    }
    let response = await this.context.createLicenceInvitationWithEmail({ Licence: this.props.licence, Email: this.state.email });
    if (response.valid()) {
      Messages.Notify.success(`The Licence item was saved successfully and the email was sent!`);
      if (this.props.goBackBtnClicked) {
        this.props.goBackBtnClicked();
      }
    } else {
      if (response.errors.length > 0) {
        Messages.Notify.error("Save failed. Server reported: " + response.errors[0].Message);
      } else {
        Messages.Notify.error("An error occurred while executing the communication");
      }
    }
  };
  inviteLink = async () => {
    let response = await this.context.createLicenceInvitationWithLink({ Licence: this.props.licence });
    if (response.valid()) {
      Messages.Notify.success(`The Licence item was saved successfully and copied to clipboard!`);
      this.setState({ completedLink: response.data.Link }, () => {
        this.setLinkCopy(false);
      });
    } else {
      if (response.errors.length > 0) {
        Messages.Notify.error("Save failed. Server reported: " + response.errors[0].Message);
      } else {
        Messages.Notify.error("An error occurred while executing the communication");
      }
    }
  };
  setLinkCopy = (showMessage: boolean) => {
    this.linkRef.current!.select();
    document.execCommand("copy");
    if (showMessage) {
      Messages.Notify.success(`Copied to clipboard!`);
    }
  };
  publisherChanged = (arg: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ publisherId: +arg.target.value });
  };
  emailChanged = (arg: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ email: arg.target.value });
  };
  formValid = () => {
    switch (this.state.currentInviteType) {
      case InviteType.DirectCreateUserId:
        return this.state.userId !== null && this.state.userId > 0;
      case InviteType.DirectCreateUsername:
        return this.state.userName !== null && !Convert.isEmptyOrSpaces(this.state.userName);
      case InviteType.InviteEmail:
        return this.state.email !== null && !Convert.isEmptyOrSpaces(this.state.email);
      case InviteType.InviteLink:
        return true;
      case InviteType.InviteCsv:
        return this.state.bulkInviteEmails.length > 0 && this.state.bulkInviteErrors.length === 0;
    }
  };
  private handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const byteArray = new Uint8Array(arrayBuffer);
        let binary = "";
        const len = byteArray.byteLength;
        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(byteArray[i]);
        }
        let csvData = btoa(binary);
        let response = await this.context.validateSubscriptionInviteEmailCsv({
          CsvData: csvData,
          AvailableLicenses: this.props.availableLicenses,
        });
        this.setState({
          bulkInviteEmails: response.data.Emails,
          bulkInviteErrors: response.data.Errors,
          pendingInvitationEmails: response.data.PendingInvitations,
        });
      };
      reader.readAsArrayBuffer(file);
    }
  };

  private sendBulkInvites = async () => {
    let targetEmails = this.state.bulkInviteEmails;
    if (this.state.pendingInvitationEmails) {
      const emailList = targetEmails.map((email, index) => <li key={index}>{email}</li>);

      let result = await Messages.Dialog.confirm(
        <div>
          <span>Invites have already been sent to the following emails. Would you like to skip these?</span>
          <ul>{emailList}</ul>
        </div>,
        "Emails already invited"
      );

      if (result === "true") {
        targetEmails = this.state.bulkInviteEmails.filter((item) => !this.state.pendingInvitationEmails.includes(item));
      }
    }
    let result2 = await Messages.Dialog.confirm("Are you sure you want to send the email invites?");

    if (result2 === "true") {
      let response = await this.context.sendBulkInvites({
        Emails: targetEmails,
        Licence: this.props.licence,
        AssignToExistingUsers: this.state.assignToExistingUsers,
      });
      if (response.valid()) {
        Messages.Notify.success(`The e-mail invites were sent succesfully!`);
      } else {
        if (response.errors.length > 0) {
          Messages.Notify.error("One or more invite(s) failed. " + response.errors[0].Message);
        } else {
          Messages.Notify.error("An error occurred while sending invites");
        }
      }
    }
  };

  assignExistingChanged = () => {
    this.setState({ assignToExistingUsers: !this.state.assignToExistingUsers });
  };
  onInputClick = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    const element = event.target as HTMLInputElement;
    element.value = "";
  };
  render() {
    let button: JSX.Element;

    switch (this.state.currentInviteType) {
      case InviteType.DirectCreateUserId:
        button = (
          <Button style={{ flex: "1" }} disabled={!this.formValid()} onClick={this.directCreateUserIdLicence} outline color="primary">
            Create Licence
          </Button>
        );
        break;
      case InviteType.DirectCreateUsername:
        button = (
          <Button style={{ flex: "1" }} disabled={!this.formValid()} onClick={this.directCreateUsername} outline color="primary">
            Create Licence
          </Button>
        );
        break;
      case InviteType.InviteEmail:
        button = (
          <Button style={{ flex: "1" }} disabled={!this.formValid()} onClick={this.sendBulkInvites} outline color="primary">
            Send invite
          </Button>
        );
        break;
      case InviteType.InviteLink:
        button = (
          <Button style={{ flex: "1" }} disabled={!this.formValid()} onClick={this.inviteLink} outline color="primary">
            Get Link
          </Button>
        );
        break;
      case InviteType.InviteCsv:
        button = (
          <Button style={{ flex: "1" }} disabled={!this.formValid()} onClick={this.sendBulkInvites} outline color="primary">
            Send Invites
          </Button>
        );
        break;
    }
    if (this.state.completedLink !== null) {
      button = <Input onClick={() => this.setLinkCopy(true)} innerRef={this.linkRef} type="text" readOnly value={this.state.completedLink} />;
    }

    let wrapper: JSX.Element;
    let pub: ILoginLibrary | null = null;
    if (this.state.publisherId === null) {
      pub = this.context.loginLibraries.get("prolibro")!;
    }
    if (this.state.publisherId === null && !pub) {
      pub = this.context.loginLibraries.rows()[0]!;
    }
    if (this.state.publisherId !== null) {
      pub = this.context.loginLibraries.rows().find((x) => x.PublisherId === this.state.publisherId)!;
    }

    let contents = (
      <Col>
        <Row>
          <p>Licences in the Dashboard can be granted in 5 ways. You may choose one of the following options:</p>
          <ol>
            <li>If you know the (case-sensitive) login name of the user and their login Publisher, you can attribute the licence directly.</li>
            <li>If you know the User ID, you can attribute the licence directly.</li>
            <li>
              If you want to send the invitation yourself, a link can be generated that you can send to the person so they can go through the acceptance steps.
            </li>
            <li>If you wish for Dashboard to contact the user by email, input the email and Dashboard will send an email invitation directly.</li>
            <li>If you have a list of emails, you can upload a CSV file, and Dashboard will send email invitations to all users in the list.</li>
          </ol>
          <p>For options 3 to 5, the licence will only become active once the user accepts it and links it to a Dashboard account.</p>
        </Row>
        <Row>
          <FormGroup style={{ marginLeft: "15px", marginRight: "15px", flex: "1" }}>
            <Input type="select" name="permissionInviteType" value={this.state.currentInviteType} onChange={this.inviteTypeChanged}>
              <option value={InviteType.DirectCreateUsername}>Invite by username</option>
              <option value={InviteType.DirectCreateUserId}>Invite by user ID</option>
              <option value={InviteType.InviteEmail}>Invite by email</option>
              <option value={InviteType.InviteLink}>Invite by link</option>
              <option value={InviteType.InviteCsv}>Invite by CSV</option>
            </Input>
          </FormGroup>
        </Row>
        {this.state.currentInviteType === InviteType.DirectCreateUserId && (
          <Row>
            <FormGroup style={{ marginLeft: "15px", marginRight: "15px", flex: "1" }}>
              <Label for="userId">User ID</Label>
              <Input type="number" name="userId" defaultValue={this.state.userId === null ? 0 : this.state.userId} onChange={this.userIdChanged} />
            </FormGroup>
          </Row>
        )}
        {this.state.currentInviteType === InviteType.DirectCreateUsername && (
          <Row>
            <FormGroup style={{ marginLeft: "15px", marginRight: "15px", flex: "1" }}>
              <Label for="userId">Username</Label>
              <Input
                type="text"
                name="username"
                defaultValue={this.state.userName === null ? undefined : this.state.userName}
                onChange={this.usernameChanged}
              />
            </FormGroup>
            <FormGroup style={{ marginLeft: "15px", marginRight: "15px", flex: "1" }}>
              <Label>Publisher the user logs in with</Label>
              <Input type="select" name="brandKey" value={pub!.PublisherId} onChange={this.publisherChanged}>
                {this.context.loginLibraries
                  .rows()
                  .sort((a, b) => (a.DisplayName > b.DisplayName ? 1 : -1))
                  .map((lib) => (
                    <option value={lib.PublisherId} key={lib.PublisherId}>
                      {lib.DisplayName}
                    </option>
                  ))}
              </Input>
            </FormGroup>
          </Row>
        )}
        {this.state.currentInviteType === InviteType.InviteEmail && (
          <Row>
            <FormGroup style={{ marginLeft: "15px", marginRight: "15px", flex: "1" }}>
              <Label for="userId">Email</Label>
              <Input type="email" name="username" defaultValue={this.state.email === null ? undefined : this.state.email} onChange={this.emailChanged} />
            </FormGroup>
          </Row>
        )}
        {this.state.currentInviteType === InviteType.InviteCsv && (
          <Row>
            <FormGroup style={{ marginLeft: "15px", marginRight: "15px", flex: "1" }}>
              <Input type="file" accept=".csv" onClick={this.onInputClick} onChange={this.handleFileUpload} />
              <Label check for="assignExisting" style={{ marginLeft: "20px" }}>
                <Input
                  onChange={this.assignExistingChanged}
                  checked={this.state.assignToExistingUsers}
                  type="checkbox"
                  name="assignExisting"
                  id="assignExisting"
                />
                Assign to Existing Users
              </Label>
            </FormGroup>
            {this.state.bulkInviteErrors.length > 0 && (
              <Alert color="danger">
                {this.state.bulkInviteErrors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </Alert>
            )}
          </Row>
        )}
        <Row className="formButtons">
          {button}
          {this.props.goBackBtnClicked && (
            <Button onClick={this.props.goBackBtnClicked} style={{ flex: "1", marginLeft: "10px" }} outline color="info">
              Go back
            </Button>
          )}
        </Row>
      </Col>
    );
    if (this.props.wrapInForm) {
      wrapper = <Form>{contents}</Form>;
    } else {
      wrapper = contents;
    }
    return wrapper;
  }
}
